import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { fetchBatches, ingestCsv } from "./api";
import {
  parseCSV,
  normalizeRows,
  normalizeQuotes,
  detectSchema,
  scoreSentiment,
  sniffDelimiter,
} from "@shared/csv";

interface Batch {
  id: string;
  source: string;
  filename: string;
  rowCount: number;
  createdAt: string;
}

interface Preview {
  filename: string;
  schema: string;
  rowCount: number;
  vectorAverages: (number | null)[]; // length 5
  sentiment: number;
  file: File;
  source: string;
}

const ZONES = [
  { key: "lucid", source: "lucid", name: "Lucidchart", color: "var(--lucid-orange)", desc: "Graded-quote export — quotes scored by dimension (1–5)." },
  { key: "copilot", source: "copilot", name: "Copilot", color: "var(--ms-blue)", desc: "Meeting quote export — Copilot-graded quotes by dimension." },
  { key: "forms", source: "forms", name: "Microsoft Forms", color: "var(--ms-blue)", desc: "Forms responses CSV — questions map to the 5 readiness vectors." },
  { key: "poll", source: "poll", name: "Poll / Template", color: "var(--violet)", desc: "Any CSV using the ai-readiness template schema." },
];

export function Ingestion() {
  const { sessionId } = useParams();
  const [batches, setBatches] = useState<Batch[]>([]);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState(false);
  const [dragZone, setDragZone] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // Which zone (and therefore which source) launched the current file pick.
  const pendingSource = useRef<string>("poll");

  const loadBatches = () => { if (sessionId) fetchBatches(sessionId).then(setBatches); };
  useEffect(loadBatches, [sessionId]);

  const avg = (vals: number[]) => (vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null);

  async function handleFile(file: File, source: string) {
    setStatus("");
    if (!/\.(csv|tsv|txt)$/i.test(file.name)) {
      setStatus("⚠ Please choose a .csv or .tsv file.");
      return;
    }
    const text = await file.text();
    const rows = parseCSV(text, sniffDelimiter(text));
    if (rows.length < 2) {
      setStatus("⚠ No data rows found.");
      return;
    }
    const schema = detectSchema(rows[0]);

    let rowCount = 0;
    let vectorAverages: (number | null)[] = [null, null, null, null, null];
    let sentiment = 0;

    if (schema === "quotes") {
      const quotes = normalizeQuotes(rows);
      rowCount = quotes.length;
      vectorAverages = [1, 2, 3, 4, 5].map((vi) =>
        avg(quotes.filter((q) => q.vectorIndex === vi).map((q) => q.score).filter((n): n is number => n !== null))
      );
      sentiment = quotes.reduce((s, q) => s + scoreSentiment(`${q.quote} ${q.why}`), 0);
    } else {
      const participants = normalizeRows(rows);
      rowCount = participants.length;
      vectorAverages = [1, 2, 3, 4, 5].map((vi) =>
        avg(participants.map((p) => p.vectorScores[vi - 1]).filter((n): n is number => n !== null))
      );
      sentiment = participants.reduce((s, p) => s + scoreSentiment(p.notes), 0);
    }

    setPreview({ filename: file.name, schema, rowCount, vectorAverages, sentiment, file, source });
  }

  async function push() {
    if (!preview || !sessionId) return;
    setBusy(true);
    setStatus("");
    try {
      const res = await ingestCsv(sessionId, preview.file, preview.source);
      setStatus(`✓ Ingested ${res.rowCount} records. Dashboard updated live.`);
      setPreview(null);
      loadBatches();
    } catch (e) {
      setStatus(`⚠ ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="app" style={{ gridTemplateRows: "68px 1fr" }}>
      <aside className="sidebar">
        <div className="sidebar-logo"><img src="/brand/ey-mark.svg" alt="EY" style={{ height: 30 }} /></div>
        <Link to={`/s/${sessionId}`} className="nav-item" title="Back to dashboard" style={{ textDecoration: "none" }}>
          <svg viewBox="0 0 24 24"><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /></svg>
        </Link>
        <div className="nav-item nav-csv active" title="Data ingestion">
          <svg viewBox="0 0 24 24"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></svg>
        </div>
      </aside>

      <header className="topbar">
        <div className="topbar-title">
          <h1>Data Ingestion Hub</h1>
          <div className="client">Multi-source CSV → live session</div>
        </div>
        <div className="topbar-spacer" />
        <Link to={`/s/${sessionId}`} className="btn-primary" style={{ textDecoration: "none", background: "var(--ms-blue)" }}>← Dashboard</Link>
      </header>

      <main style={{ gridColumn: 2, gridRow: 2, overflow: "auto", padding: 24, display: "grid", gridTemplateColumns: "1fr 320px", gap: 20, background: "var(--bg)" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <input ref={inputRef} type="file" accept=".csv,.tsv,.txt,text/csv" style={{ display: "none" }} onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0], pendingSource.current)} />

          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 12 }}>
            {ZONES.map((z) => (
              <div
                key={z.key}
                className={`panel ${dragZone === z.key ? "drag-over" : ""}`}
                style={{ padding: 18, cursor: "pointer", border: `2px dashed ${dragZone === z.key ? z.color : "var(--border)"}`, alignItems: "center", textAlign: "center", gap: 8 }}
                onClick={() => { pendingSource.current = z.source; inputRef.current?.click(); }}
                onDragOver={(e) => { e.preventDefault(); setDragZone(z.key); }}
                onDragLeave={() => setDragZone(null)}
                onDrop={(e) => { e.preventDefault(); setDragZone(null); const f = e.dataTransfer.files[0]; if (f) handleFile(f, z.source); }}
              >
                <div style={{ width: 40, height: 40, borderRadius: 10, border: `1.5px solid ${z.color}`, display: "flex", alignItems: "center", justifyContent: "center", color: z.color }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>
                </div>
                <div style={{ fontSize: 14, fontWeight: 800, color: "var(--fg-inv)" }}>{z.name}</div>
                <div style={{ fontSize: 11, color: "var(--muted)", lineHeight: 1.5 }}>{z.desc}</div>
              </div>
            ))}
          </div>

          {preview && (
            <div className="panel" style={{ padding: 20, gap: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: "var(--fg-inv)" }}>Preview — {preview.filename}</div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button className="modal-cancel" onClick={() => setPreview(null)}>Discard</button>
                  <button className="btn-primary" onClick={push} disabled={busy}>{busy ? "Pushing…" : "Push to dashboard →"}</button>
                </div>
              </div>
              <div style={{ display: "flex", gap: 16, fontSize: 12, color: "var(--muted)" }}>
                <span>Schema: <strong style={{ color: "var(--fg-inv)" }}>{preview.schema === "quotes" ? "graded quotes" : preview.schema}</strong></span>
                <span>Source: <strong style={{ color: "var(--fg-inv)" }}>{preview.source}</strong></span>
                <span>Rows: <strong style={{ color: "var(--fg-inv)" }}>{preview.rowCount}</strong></span>
                <span>Sentiment: <strong style={{ color: preview.sentiment >= 0 ? "var(--green)" : "var(--red)" }}>{preview.sentiment >= 0 ? `+${preview.sentiment}` : preview.sentiment}</strong></span>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 8 }}>
                {preview.vectorAverages.map((a, i) => (
                  <div key={i} style={{ background: "var(--surface-dim)", borderRadius: 8, padding: "8px 10px", textAlign: "center" }}>
                    <div style={{ fontSize: 10, color: "var(--muted)" }}>V{i + 1}</div>
                    <div style={{ fontSize: 18, fontWeight: 800, color: "var(--fg-inv)" }}>{a === null ? "—" : a.toFixed(1)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {status && <div className="panel" style={{ padding: "12px 16px", fontSize: 13, color: status.startsWith("✓") ? "var(--green)" : "var(--red)" }}>{status}</div>}
        </div>

        {/* History */}
        <div className="panel" style={{ padding: 18, gap: 8, alignSelf: "start" }}>
          <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: ".06em", textTransform: "uppercase", color: "var(--fg-inv)", marginBottom: 6 }}>Ingestion History</div>
          {batches.length ? (
            batches.map((b) => (
              <div key={b.id} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid var(--border)", fontSize: 12 }}>
                <div>
                  <div style={{ color: "var(--fg-inv)", fontWeight: 600 }}>{b.filename}</div>
                  <div style={{ color: "var(--muted)", fontSize: 11 }}>{b.source} · {new Date(b.createdAt).toLocaleString()}</div>
                </div>
                <div style={{ color: "var(--ms-blue)", fontWeight: 700 }}>{b.rowCount}</div>
              </div>
            ))
          ) : (
            <div style={{ fontSize: 12, color: "var(--muted)" }}>No imports yet.</div>
          )}
        </div>
      </main>
    </div>
  );
}
